/**
 * MemberProfileGate — la porte qu'un membre INVITÉ franchit avant d'entrer.
 *
 *   pas visé par le parcours (équipe en place / founder / superadmin) → children
 *   Discord non vérifié                        → écran de finalisation
 *   Discord vérifié, accueil pas confirmé      → children + panneau d'accueil à droite
 *   accueil confirmé                           → children
 *
 * 🩸 Il y avait une seconde porte, le TEST (19-08-2026). Retirée le 06-10-2026 :
 * le cockpit s'ouvre dès Discord, et le test se passe derrière un ticket,
 * envoyé par Francis — sans lui, pas de shift. Cf `resoudreEcranDuGate`.
 *
 * Discord : `members.myProfileGate`, une QUERY réactive — dès que le callback
 * OAuth stampe `discordVerifiedAt`, ce gate re-render tout seul. Elle ne throw
 * jamais.
 */
import { type ReactNode } from "react";
import { useQuery } from "convex/react";

import { api } from "../../../convex/_generated/api";
import {
  resoudreEcranDuGate,
  type EcranDuGate,
} from "../../../convex/_lib/profileGate";
import { useActiveAccountId } from "./activeAccount";
import { useSession } from "./useSession";
import { AuthLoadingScreen } from "./AuthLoadingScreen";
import { MemberProfileCompletion } from "./MemberProfileCompletion";

export function MemberProfileGate({ children }: { children: ReactNode }) {
  const accountId = useActiveAccountId();
  const { signOut } = useSession();
  const gate = useQuery(
    api.members.myProfileGate,
    accountId ? { accountId } : "skip",
  );
  const exigeDiscord = gate?.requiresCompletion === true;
  // `=== true` : un Convex d'avant l'accueil ne renvoie pas le champ, et
  // l'écran doit alors s'effacer dès Discord, comme avant.
  const accueilAFaire = gate?.accueilAFaire === true;

  // Query en vol → loader, pour ne pas flasher le shell à un invité qu'on va
  // bloquer une seconde plus tard.
  if (accountId && gate === undefined) return <AuthLoadingScreen />;

  const ecran: EcranDuGate = resoudreEcranDuGate({ exigeDiscord });

  if (gate?.membershipId && accountId && (ecran === "discord" || accueilAFaire)) {
    const accueil = (
      <MemberProfileCompletion
        accountId={accountId}
        membershipId={gate.membershipId}
        gate={gate}
        onSignOut={() => void signOut()}
      />
    );
    // Discord fait : le VRAI cockpit, et l'accueil en panneau à droite par-dessus
    // (cf `AccueilDuChatteur`). Avant Discord, la porte seule.
    if (ecran === "discord") return accueil;
    return (
      <>
        {children}
        {accueil}
      </>
    );
  }

  return <>{children}</>;
}
